import { useState, useEffect, ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface AdminRouteProps {
  children: ReactNode;
}

const AdminRoute = ({ children }: AdminRouteProps) => {
  const { toast } = useToast();
  const [state, setState] = useState<"loading" | "authorized" | "unauthorized" | "no_session">("loading");

  useEffect(() => {
    const check = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setState("no_session");
        return;
      }

      // Require admin role only
      const { data: isAdmin } = await supabase.rpc("has_role", {
        _user_id: session.user.id,
        _role: "admin",
      });

      if (!isAdmin) {
        toast({
          title: "403 — Access Denied",
          description: "Admin role is required.",
          variant: "destructive",
        });
        setState("unauthorized");
        return;
      }

      setState("authorized");
    };

    check();
  }, [toast]);

  if (state === "loading") {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-background"
        role="status"
        aria-label="Verifying admin access"
      >
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        <span className="sr-only">Verifying admin access...</span>
      </div>
    );
  }

  if (state === "no_session") {
    return <Navigate to="/auth" replace />;
  }

  if (state === "unauthorized") {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

export default AdminRoute;
